import {
  deleteGuardianNotification,
  fetchGuardianNotifications,
  markAllGuardianNotificationsRead,
  markGuardianNotificationRead,
} from '@/features/guardian/services/notifications';
import { useSessionStore } from '@/stores';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import type { GuardianNotificationsResponse } from '../notifications/types';
import { guardianKeys } from './query-keys';

const NOTIFICATIONS_PAGE_SIZE = 20;

type NotificationsCache = InfiniteData<GuardianNotificationsResponse, string | undefined>;

export function useGuardianNotificationsQuery() {
  const guardianId = useSessionStore(state => state.user?.uid);

  return useInfiniteQuery({
    queryKey: guardianKeys.notifications(guardianId),
    queryFn: ({ pageParam }) =>
      fetchGuardianNotifications({
        limit: NOTIFICATIONS_PAGE_SIZE,
        cursor: pageParam,
        paginate: true,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
    enabled: Boolean(guardianId),
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

function updateNotificationPages(
  cache: NotificationsCache | undefined,
  update: (page: GuardianNotificationsResponse) => GuardianNotificationsResponse,
) {
  return cache ? { ...cache, pages: cache.pages.map(update) } : cache;
}

function useNotificationMutationCache() {
  const guardianId = useSessionStore(state => state.user?.uid);
  const queryClient = useQueryClient();
  const queryKey = guardianKeys.notifications(guardianId);

  return { queryClient, queryKey };
}

export function useMarkGuardianNotificationReadMutation() {
  const { queryClient, queryKey } = useNotificationMutationCache();

  return useMutation({
    mutationFn: markGuardianNotificationRead,
    onMutate: async id => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<NotificationsCache>(queryKey);
      const target = previous?.pages
        .flatMap(page => page.notifications)
        .find(item => item.id === id);
      queryClient.setQueryData<NotificationsCache>(queryKey, cache =>
        updateNotificationPages(cache, page => {
          if (!target || target.read) return page;
          return {
            ...page,
            unreadCount: Math.max(0, page.unreadCount - 1),
            notifications: page.notifications.map(item =>
              item.id === id ? { ...item, read: true } : item,
            ),
          };
        }),
      );
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
}

export function useMarkAllGuardianNotificationsReadMutation() {
  const { queryClient, queryKey } = useNotificationMutationCache();

  return useMutation({
    mutationFn: markAllGuardianNotificationsRead,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<NotificationsCache>(queryKey);
      queryClient.setQueryData<NotificationsCache>(queryKey, cache =>
        updateNotificationPages(cache, page => ({
          ...page,
          unreadCount: 0,
          notifications: page.notifications.map(item => ({ ...item, read: true })),
        })),
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
}

export function useDeleteGuardianNotificationMutation() {
  const { queryClient, queryKey } = useNotificationMutationCache();

  return useMutation({
    mutationFn: deleteGuardianNotification,
    onMutate: async id => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<NotificationsCache>(queryKey);
      const target = previous?.pages
        .flatMap(page => page.notifications)
        .find(item => item.id === id);
      queryClient.setQueryData<NotificationsCache>(queryKey, cache =>
        updateNotificationPages(cache, page => {
          return {
            ...page,
            unreadCount:
              target && !target.read ? Math.max(0, page.unreadCount - 1) : page.unreadCount,
            notifications: page.notifications.filter(item => item.id !== id),
          };
        }),
      );
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
}
